import { OrderCrud } from '@diamond/shared/schema/order';
export * from './calculate-free-goods';
import {
  CartTotalResponseBundledDiscount,
  CartTotalResponseBundledFreeGoods,
  CartTotalResponsePromo,
  DetailProduct,
  DiscountPromoItem,
  FreeGoodsPromoItem,
  Order,
} from '@diamond/shared/types';
import {
  buyerCurrencyFormatter,
  promoValidFromToFormat,
} from '@diamond/shared/utils';

type DiscountCashbackItem = {
  duration: string;
  text: string;
  promoId: string;
};

export type ItemInfo = {
  id: string;
  img?: string;
  price: number;
  qty: number;
  uom: string;
};

type FreeGoodsItem = {
  duration: string;
  text: string;
  promoId: string;
  item: ItemInfo;
};

export type ProcessedPromo = {
  freeGoods: Array<FreeGoodsItem>;
  discount: Array<DiscountCashbackItem>;
  cashback: Array<DiscountCashbackItem>;
  bundledFreeGoods: Array<FreeGoodsItem>;
  bundledDiscount: Array<DiscountCashbackItem>;
  bundledCashback: Array<DiscountCashbackItem>;
  hasMultiFreeGoods: boolean;
  hasFreeGoods: boolean;
  hasBundledFreeGoods: boolean;
  hasDiscount: boolean;
  hasBundledDiscount: boolean;
  hasCashback: boolean;
  hasBundledCashback: boolean;
  priceAfterDiscount: number;
  minOrderQty: number;
  qtyFreeGoods: number;
  isVoucherFreeGoods?: boolean;
};

const STOCK_MAGNIFIER = 1000;

export function constructDiscountText<T extends DiscountPromoItem>(
  item: T,
  productSalesUnit: string,
  productTitle?: string
): string {
  const discountAmount = item.deduction_amount / 100;
  const minimumOrderAmount = item.minimum_order_amount;
  const minimumOrderQty = item.minimum_order_quantity / STOCK_MAGNIFIER;

  /**
   * Check if minimum order is using amount or `qty`.
   * In case of `qty` we append the measurement unit
   */
  const textMinimumOrder = minimumOrderAmount
    ? `${buyerCurrencyFormatter(minimumOrderAmount)}`
    : `${minimumOrderQty} ${productSalesUnit.toLowerCase()}`;

  /**
   * Check if `is_applies_in_multiples`
   */
  const textAppliesMultiples = item.is_applies_in_multiples
    ? '(Berlaku kelipatan)'
    : '';

  /**
   * Check if productTitle params exist
   */
  const textProductTitle = productTitle ? ` ${productTitle}` : '';

  if (item.is_discount) {
    return `Potongan ${discountAmount}% untuk minimum pembelian ${textMinimumOrder}${textProductTitle} ${textAppliesMultiples}`.trim();
  }

  return `Potongan ${buyerCurrencyFormatter(
    item.deduction_amount
  )} untuk minimum pembelian ${textMinimumOrder}${textProductTitle} ${textAppliesMultiples}`.trim();
}

export function constructFreeGoodsText<T extends FreeGoodsPromoItem>(
  item: T,
  productSalesUnit: string,
  productTitle?: string
): string {
  const minimumOrderAmount = item.minimum_order_amount;
  const minimumOrderQty = item.minimum_order_quantity / STOCK_MAGNIFIER;
  const qtyFreeGoods = (item.quantity_free_goods || 0) / STOCK_MAGNIFIER;

  /**
   * Check if minimum order is using amount or `qty`.
   * In case of `qty` we append the measurement unit
   */
  const textMinimumOrder = minimumOrderAmount
    ? `${buyerCurrencyFormatter(minimumOrderAmount)}`
    : `${minimumOrderQty} ${productSalesUnit.toLowerCase()}`;

  /**
   * Check if `is_applies_in_multiples`
   */
  const textAppliesMultiples = item.is_applies_in_multiples
    ? '(Berlaku kelipatan)'
    : '';

  /**
   * Check if productTitle params exist
   */
  const textProductTitle = productTitle ? ` ${productTitle}` : '';

  return `Beli ${textMinimumOrder}${textProductTitle} Gratis ${qtyFreeGoods} ${item.sales_unit_desc.toLowerCase()} ${
    item.title
  } ${textAppliesMultiples}`.trim();
}

type FreeGoodsable = Pick<
  FreeGoodsPromoItem,
  | 'valid_from'
  | 'valid_until'
  | 'mechanism'
  | 'id'
  | 'product_id'
  | 'price'
  | 'product_image_thumbnails'
  | 'quantity_free_goods'
  | 'sales_unit_desc'
>;

export function createFreeGoodsItem<T extends FreeGoodsable>(
  item: T
): FreeGoodsItem {
  return {
    duration: promoValidFromToFormat(item.valid_from, item.valid_until),
    text:
      `${item.mechanism} ${
        'is_applies_in_multiples' in item && item.is_applies_in_multiples
          ? '(Berlaku kelipatan)'
          : ''
      }`.trim() || '',
    promoId: item.id,
    item: {
      id: item.product_id,
      price: item.price,
      img: item.product_image_thumbnails[0],
      qty: (item.quantity_free_goods || 0) / STOCK_MAGNIFIER,
      uom: item.sales_unit_desc,
    },
  };
}

type Discountable = Pick<
  DiscountPromoItem,
  'valid_from' | 'valid_until' | 'mechanism' | 'id' | 'is_discount'
>;

export function createDiscountItem<T extends Discountable>(
  item: T
): DiscountCashbackItem {
  return {
    duration: promoValidFromToFormat(item.valid_from, item.valid_until),
    text:
      `${item.mechanism} ${
        'is_applies_in_multiples' in item && item.is_applies_in_multiples
          ? '(Berlaku kelipatan)'
          : ''
      }`.trim() || '',
    promoId: item.id,
  };
}

export function createBundledFreeGoodsItem(
  item: CartTotalResponseBundledFreeGoods
): FreeGoodsItem {
  return {
    duration: promoValidFromToFormat(item.valid_from, item.valid_until),
    text: item.mechanism || '',
    promoId: item.id,
    item: {
      id: item.product_id,
      price: item.price,
      img: item.product_image_thumbnails[0],
      qty: (item.quantity_free_goods || 0) / STOCK_MAGNIFIER,
      uom: item.sales_unit_desc,
    },
  };
}

export function createBundledDiscountItem(
  item: CartTotalResponseBundledDiscount
): DiscountCashbackItem {
  return {
    duration: promoValidFromToFormat(item.valid_from, item.valid_until),
    text: item.mechanism || '',
    promoId: item.id,
  };
}

function processDiscounts<T extends Discountable>(promos: Array<T>) {
  return promos.reduce(
    (acc, curr) => {
      const discountItem = createDiscountItem(curr);
      if (curr.is_discount) {
        acc.discount.push(discountItem);
      } else {
        acc.cashback.push(discountItem);
      }
      return acc;
    },
    { cashback: [], discount: [] } as Pick<
      ProcessedPromo,
      'cashback' | 'discount'
    >
  );
}

function processFreeGoods<T extends FreeGoodsable>(promos: Array<T>) {
  return promos.reduce((acc, curr) => {
    const freeGoodsItem = createFreeGoodsItem(curr);
    acc.push(freeGoodsItem);
    return acc;
  }, [] as Array<FreeGoodsItem>);
}

type Promoable = Pick<
  DetailProduct,
  | 'discount_promos'
  | 'free_good_promos'
  | 'bundled_discount_promos'
  | 'bundled_free_good_promos'
>;

export const defaultProcessedPromoValue: ProcessedPromo = {
  hasMultiFreeGoods: false,
  hasDiscount: false,
  hasBundledDiscount: false,
  hasCashback: false,
  hasBundledCashback: false,
  hasFreeGoods: false,
  hasBundledFreeGoods: false,
  cashback: [],
  discount: [],
  freeGoods: [],
  bundledCashback: [],
  bundledDiscount: [],
  bundledFreeGoods: [],
  priceAfterDiscount: 0,
  minOrderQty: 0,
  qtyFreeGoods: 0,
  isVoucherFreeGoods: false,
};

export function processPromos<T extends Promoable>(data?: T): ProcessedPromo {
  if (!data) return defaultProcessedPromoValue;

  const _hasDiscount = data.discount_promos.length > 0;
  const _hasBundledDiscount = data.bundled_discount_promos.length > 0;
  const _hasFreeGoods = data.free_good_promos.length > 0;
  const _hasBundledFreeGoods = data.bundled_free_good_promos.length > 0;
  const hasMultiFreeGoods = _hasFreeGoods && _hasBundledFreeGoods;

  const { cashback, discount } = _hasDiscount
    ? processDiscounts(data.discount_promos)
    : { cashback: [], discount: [] };

  const { cashback: bundledCashback, discount: bundledDiscount } =
    _hasBundledDiscount
      ? processDiscounts(data.bundled_discount_promos)
      : { cashback: [], discount: [] };

  const freeGoods = _hasFreeGoods
    ? processFreeGoods(data.free_good_promos)
    : [];

  const bundledFreeGoods = _hasBundledFreeGoods
    ? processFreeGoods(data.bundled_free_good_promos)
    : [];

  const hasFreeGoods = freeGoods.length > 0;
  const hasBundledFreeGoods = bundledFreeGoods.length > 0;
  const hasDiscount = discount.length > 0;
  const hasBundledDiscount = bundledDiscount.length > 0;
  const hasCashback = cashback.length > 0;
  const hasBundledCashback = bundledCashback.length > 0;

  const priceAfterDiscount =
    hasDiscount || hasCashback
      ? data.discount_promos[0]['price_after_discount'] || 0
      : 0;

  // Prioritize single promos if any
  const minOrderQty = hasFreeGoods
    ? data.free_good_promos[0].minimum_order_quantity / STOCK_MAGNIFIER
    : hasBundledFreeGoods
    ? data.bundled_free_good_promos[0].sum_minimum_order_quantity /
      STOCK_MAGNIFIER
    : 0;

  // Prioritize single promos if any
  const qtyFreeGoods = hasFreeGoods
    ? (data.free_good_promos[0]?.quantity_free_goods || 0) / STOCK_MAGNIFIER
    : hasBundledFreeGoods
    ? (data.bundled_free_good_promos[0]?.quantity_free_goods || 0) /
      STOCK_MAGNIFIER
    : 0;

  return {
    freeGoods,
    cashback,
    discount,
    bundledCashback,
    bundledDiscount,
    bundledFreeGoods,
    hasMultiFreeGoods,
    hasFreeGoods,
    hasBundledFreeGoods,
    hasDiscount,
    hasBundledDiscount,
    hasCashback,
    hasBundledCashback,
    priceAfterDiscount,
    minOrderQty,
    qtyFreeGoods,
  };
}

type InvalidPromoOrderValidation =
  OrderCrud['getOrderValidation']['response']['invalid_promos'];

export function processPromoValidation(
  data: InvalidPromoOrderValidation | CartTotalResponsePromo | undefined
): ProcessedPromo {
  if (
    !data ||
    (data.single_free_good_promos.length === 0 &&
      data.single_discount_promos.length === 0 &&
      data.bundled_free_good_promos.length === 0 &&
      data.bundled_discount_promos.length === 0)
  )
    return defaultProcessedPromoValue;

  const mappedPromo: Promoable = {
    discount_promos:
      data.single_discount_promos.map((promo) => ({
        ...promo,
        is_applies_in_multiples:
          'is_applies_in_multiples' in promo && promo.is_applies_in_multiples,
      })) || [],
    free_good_promos:
      data.single_free_good_promos.map((promo) => ({
        ...promo,
        is_applies_in_multiples:
          'is_applies_in_multiples' in promo && promo.is_applies_in_multiples,
      })) || [],
    bundled_discount_promos: data.bundled_discount_promos || [],
    bundled_free_good_promos: data.bundled_free_good_promos || [],
  };

  return processPromos(mappedPromo);
}

type ValidPropertyKeys<T> = {
  [k in keyof T]: T[k] extends PropertyKey ? k : never;
}[keyof T];

function normalizeProcessedPromo<T>(
  promos: Array<T>,
  identifier: ValidPropertyKeys<T>
) {
  const entry: Record<string, T> = Object.fromEntries(
    promos.map((p) => [p[identifier], p])
  );
  const order = promos.map((p) => p[identifier]);
  return [entry, order] as const;
}

function normalizeAndMergePromo<T>(
  staticProcessedPromo: Array<T>,
  cartTotalProcessedPromo: Array<T>,
  identifier: ValidPropertyKeys<T>
) {
  const [staticEntry, staticOrder] = normalizeProcessedPromo(
    staticProcessedPromo,
    identifier
  );
  const [cartEntry] = normalizeProcessedPromo(
    cartTotalProcessedPromo,
    identifier
  );

  const mergedEntry = { ...staticEntry, ...cartEntry };

  return [mergedEntry, staticOrder] as const;
}

export function mergeProcessPromoWithCartTotal(
  staticProcessedPromo: ProcessedPromo,
  cartTotalProcessedPromo: ProcessedPromo
): ProcessedPromo {
  const [mergedFreeGoodsEntry, freeGoodsOrder] = normalizeAndMergePromo(
    staticProcessedPromo.freeGoods,
    cartTotalProcessedPromo.freeGoods,
    'promoId'
  );

  const [mergedDiscountEntry, discountOrder] = normalizeAndMergePromo(
    staticProcessedPromo.discount,
    cartTotalProcessedPromo.discount,
    'promoId'
  );

  const [mergedCashbackEntry, cashbackOrder] = normalizeAndMergePromo(
    staticProcessedPromo.cashback,
    cartTotalProcessedPromo.cashback,
    'promoId'
  );

  const [mergedBundledFreeGoodsEntry, bundledFreeGoodsOrder] =
    normalizeAndMergePromo(
      staticProcessedPromo.bundledFreeGoods,
      cartTotalProcessedPromo.bundledFreeGoods,
      'promoId'
    );

  const [mergedBundledDiscountEntry, bundledDiscountOrder] =
    normalizeAndMergePromo(
      staticProcessedPromo.bundledDiscount,
      cartTotalProcessedPromo.bundledDiscount,
      'promoId'
    );

  const [mergedBundledCashbackEntry, bundledCashbackOrder] =
    normalizeAndMergePromo(
      staticProcessedPromo.bundledCashback,
      cartTotalProcessedPromo.bundledCashback,
      'promoId'
    );

  return {
    ...staticProcessedPromo,
    freeGoods: freeGoodsOrder.map((promoId) => mergedFreeGoodsEntry[promoId]),
    discount: discountOrder.map((promoId) => mergedDiscountEntry[promoId]),
    cashback: cashbackOrder.map((promoId) => mergedCashbackEntry[promoId]),
    bundledFreeGoods: bundledFreeGoodsOrder.map(
      (promoId) => mergedBundledFreeGoodsEntry[promoId]
    ),
    bundledDiscount: bundledDiscountOrder.map(
      (promoId) => mergedBundledDiscountEntry[promoId]
    ),
    bundledCashback: bundledCashbackOrder.map(
      (promoId) => mergedBundledCashbackEntry[promoId]
    ),
    minOrderQty:
      cartTotalProcessedPromo.minOrderQty || staticProcessedPromo.minOrderQty,
    qtyFreeGoods:
      cartTotalProcessedPromo.qtyFreeGoods || staticProcessedPromo.qtyFreeGoods,
  };
}

export type OrderDetailData = Order['items'];
export type OrderDetailDataWithDivider = Array<
  Omit<OrderDetailData[number], 'division'> & {
    divider: string;
  }
>;
type ProductsWithPromos = Array<
  OrderDetailDataWithDivider[number]['products'][number] & {
    free_good_data: SingleFreeGoodsPromo | undefined;
    hasDiscount: boolean;
    hasCashback: boolean;
    compiled_discount: CompiledProductDiscounts[];
  }
>;

export type BindedPromoData = Array<
  Omit<OrderDetailDataWithDivider[number], 'products'> & {
    products: ProductsWithPromos;
  }
>;
export type SingleFreeGoodsPromo =
  OrderDetailData[number]['promos']['single_free_good_promos'][number];
export type BundledFreeGoodsPromo =
  OrderDetailData[number]['promos']['bundled_free_good_promos'][number];
export type SingleDiscountPromo =
  OrderDetailData[number]['promos']['single_discount_promos'][number];
export type BundledDiscountPromo =
  OrderDetailData[number]['promos']['bundled_discount_promos'][number];
export type CompiledProductDiscounts =
  OrderDetailData[number]['promos']['compiled_product_discounts'][number];
export type ProductWithPromo = BindedPromoData[number]['products'][number];
export type PromoWithProduct = {
  product?: {
    sales_unit_desc: string;
    title: string;
  };
};
type Promo = (
  | Pick<SingleFreeGoodsPromo, keyof SingleFreeGoodsPromo>
  | Pick<CompiledProductDiscounts, keyof CompiledProductDiscounts>
) &
  PromoWithProduct;

type PromoTypes = keyof OrderDetailData[number]['promos'];

export type ExtendedOrderPromoWithProduct = {
  [T in PromoTypes]: (OrderDetailData[number]['promos'][T][number] &
    PromoWithProduct)[];
};

// Bind single discount and single free goods promos to order detail products
export function bindPromoData(data: OrderDetailDataWithDivider) {
  data.forEach((division) => {
    const { products, promos } = division;

    products.forEach((product) => {
      let free_good_data = null;
      let hasDiscount = false;
      let hasCashback = false;
      const compiled_discount: CompiledProductDiscounts[] = [];

      // Check single free good promos
      (promos.single_free_good_promos as Promo[]).forEach((promo) => {
        if ((promo as SingleFreeGoodsPromo).ref_product_id === product.id) {
          free_good_data = { ...(promo as SingleFreeGoodsPromo) };
        }
      });

      // Check compiled discount promos
      (promos.compiled_product_discounts as CompiledProductDiscounts[]).forEach(
        (promo) => {
          if (promo.product_id === product.id) {
            promo.is_discount ? (hasDiscount = true) : (hasCashback = true);
            compiled_discount.push(promo as CompiledProductDiscounts);
          }
        }
      );

      Object.assign(product, {
        free_good_data,
        hasDiscount,
        hasCashback,
        compiled_discount,
      });
    });
  });

  return data as BindedPromoData;
}
